// @flow

import gql from 'graphql-tag';
import { get } from 'lodash';

import { useQuery } from '@apollo/react-hooks';
import {
  type PaymentSource,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment';

type Input = {
  accountId: number,
};

type Output = {
  error: ?string,
  forceSourceEdit: boolean,
  isLoading: boolean,
  paymentSources: Array<PaymentSource>,
  primarySource: ?PaymentSource,
  supportedPaymentMethods: Array<SupportedPaymentMethod>,
};

/* eslint-disable graphql/no-deprecated-fields */
const paymentMethodsQuery = gql`
  query paymentMethodsQuery($id: Int!) {
    account(id: $id) {
      id
      paymentSources {
        backend
        details {
          ... on PaymentSourceCard {
            addressCountry
          }
        }
        id
        type
      }
      supportedPaymentMethodsv2 {
        type
        backend
      }
    }
  }
`;
/* eslint-enable graphql/no-deprecated-fields */

const usePaymentSource = ({ accountId }: Input): Output => {
  const queryResults = useQuery(paymentMethodsQuery, {
    skip: !accountId,
    variables: {
      id: accountId,
    },
  });

  const paymentSources = get(
    queryResults,
    ['data', 'account', 'paymentSources'],
    []
  );
  const primarySource = paymentSources[0];
  const forceSourceEdit = Boolean(
    primarySource &&
      primarySource.method === paymentSourceMethods.card &&
      !primarySource.addressCountry
  );

  return {
    error: queryResults.error
      ? 'There was an issue loading your payment sources. Please try again'
      : null,
    forceSourceEdit,
    isLoading: queryResults.loading,
    paymentSources,
    primarySource,
    supportedPaymentMethods: get(
      queryResults,
      ['data', 'account', 'supportedPaymentMethodsv2'],
      []
    ),
  };
};

export default usePaymentSource;
